import {
  Box,
  Button,
  Flex,
  FormControl,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { Formik } from "formik";
import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSignUpUserMutation } from "store/auth/authApi";
import { signUpschema } from "utils/schema";
import HustleLogo from "../assets/images/Hustle.png";

function SignUp() {
  const [showPassword, setShowPassword] = useState(false);
  const [signUpUser, { data: signUpResponse, error, isLoading }] =
    useSignUpUserMutation();
  const userInfo = useSelector((state) => state.auth?.userInfo);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const toast = useToast();

  // THEME COLORS
  const bodyBackground = useColorModeValue("#ffffff", "#0b0420");
  const textColor = useColorModeValue("brand.900", "brand.500");
  const subCopy = useColorModeValue("gray.700", "gray.400"); // content text

  const placeholderColor = useColorModeValue("#A192B1", "#8374C1");
  const inputBorder = useColorModeValue("#A192B1", "#4B3A75");
  const borderColor = useColorModeValue("#401E68", "#8374C1");

  useEffect(() => {
    if (userInfo) navigate(from);
  }, [userInfo]);

  useEffect(() => {
    if (error) {
      toast({
        position: "top-right",
        title: "Error",
        description: error.message || "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }

    if (signUpResponse?.data?._id) {
      toast({
        position: "top-right",
        title: "Account created",
        description: "Please check your email to verify.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setTimeout(() => {
        navigate("/signIn");
      }, 1000);
    }
  }, [error, signUpResponse]);

  return (
    <Box bg={bodyBackground} minH="100vh" color={textColor}>
      <Flex direction="column" align="center" pt={8} mb={4}>
        <img src={HustleLogo} alt="Hustle Logo" style={{ width: "180px" }} />
      </Flex>

      <Formik
        validationSchema={signUpschema}
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          password: "",
        }}
        onSubmit={async (values) => {
          const trimmedValues = Object.fromEntries(
            Object.entries(values).map(([key, val]) => [key, val.trim?.() || val])
          );
          await signUpUser(trimmedValues);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <Flex
            justify="center"
            align="center"
            minH="calc(100vh - 100px)"
            px={4}
          >
            <Flex
              direction={{ base: "column", md: "row" }}
              w="100%"
              maxW="80%"
              align="center"
              justify="space-between"
              gap={10}
            >
              {/* LEFT FORM */}
              <Flex
                direction="column"
                border={`2px solid ${borderColor}`}
                borderRadius="30px"
                color={textColor}
                px={{ base: "24px", md: "60px" }}
                py={{ base: "24px", md: "40px" }}
                boxShadow={`10px 10px 0 ${borderColor}`}
                flex="1"
              >
                <Text
                  fontSize="30px"
                  fontWeight="bold"
                  color={textColor}
                  mb={8}
                  textAlign="center"
                >
                  Let's Get You Signed Up
                </Text>

                {["firstName", "lastName", "email", "phoneNumber"].map(
                  (field, index) => (
                    <FormControl isRequired key={field} mb={4}>
                      <Input
                        name={field}
                        placeholder={field
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (s) => s.toUpperCase())}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values[field]}
                        isInvalid={touched[field] && errors[field]}
                        borderRadius="full"
                        fontSize="16px"
                        border={`2px solid ${inputBorder}`}
                        color={textColor}
                        bg="transparent"
                        _placeholder={{ color: placeholderColor }}
                        _focus={{
                          borderColor: "#9b4dff",
                          boxShadow: "0 0 6px #9b4dff",
                        }}
                      />
                    </FormControl>
                  )
                )}

                {/* PASSWORD */}
                <FormControl isRequired mb={6}>
                  <InputGroup>
                    <Input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      isInvalid={touched.password && errors.password}
                      borderRadius="full"
                      fontSize="16px"
                      border={`2px solid ${inputBorder}`}
                      color={textColor}
                      bg="transparent"
                      _placeholder={{ color: placeholderColor }}
                      _focus={{
                        borderColor: "#9b4dff",
                        boxShadow: "0 0 6px #9b4dff",
                      }}
                    />
                    <InputRightElement h="full">
                      <Button
                        variant="unstyled"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <ViewIcon color="#9b4dff" />
                        ) : (
                          <ViewOffIcon color="#9b4dff" />
                        )}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  type="submit"
                  w="full"
                  size="lg"
                  borderRadius="full"
                  colorScheme="brand"
                  bgClip="padding-box"
                  onClick={handleSubmit}
                  isLoading={isLoading}
                >
                  Sign up
                </Button>

                <Text mt={8} textAlign="center" fontSize="14px" color={subCopy}>
                  Already have an account?
                  <Link
                    as={RouterLink}
                    to="/signIn"
                    fontWeight="bold"
                    ml={1}
                    color={textColor}
                  >
                    Sign in
                  </Link>
                </Text>
              </Flex>

              {/* RIGHT CONTENT */}
              <Box
                flex="1.5"
                textAlign={{ base: "center", md: "left" }}
                px={{ md: 6 }}
              >
                <Text
                  fontSize={{ base: "28px", md: "72px" }}
                  fontWeight="light"
                  color={textColor}
                  lineHeight="1.2"
                >
                  Let's Get Hustlin'
                </Text>
                <Text fontSize={{ base: "16px", md: "24px" }} color={subCopy}>
                  Unlock that 30% better ROAS
                </Text>
                <Text fontSize="80px">💰 🎯 💸</Text>
              </Box>
            </Flex>
          </Flex>
        )}
      </Formik>
    </Box>
  );
}

export default SignUp;